<script setup lang="ts">
/**
 * design-system.md -> Components -> Avatar and tile. Tenants are square. `size` is 20 (rail list),
 * 32 (mail-admin-handoff-v2-changes/CHANGES.md §2 -> the Find tenant palette's result rows), 36
 * (tenant tile in a table list) or 52 (tenant header) — default 36. `tone` is "tile" (default,
 * neutral `tile` fill for a list), "ink" (the tenant header's 52px tile, and the palette's
 * highlighted row), "rail" (inactive tenant tile on the rail) or "signal" (the active tenant's tile
 * on the rail, lime with ink text). The glyph is the first letter of `name`, uppercased.
 */
import { computed } from 'vue';

const props = withDefaults(defineProps<{ name: string; size?: 20 | 32 | 36 | 52; tone?: 'tile' | 'ink' | 'rail' | 'signal' }>(), {
  size: 36,
  tone: 'tile'
});

const initial = computed(() => (props.name.trim().charAt(0) || '?').toUpperCase());

const radiusBySize: Record<20 | 32 | 36 | 52, string> = { 20: '5px', 32: '8px', 36: '9px', 52: '12px' };
const fontBySize: Record<20 | 32 | 36 | 52, string> = { 20: '11px', 32: '14px', 36: '16px', 52: '24px' };

const toneClasses: Record<string, string> = {
  tile: 'bg-tile text-ink',
  ink: 'bg-ink text-on-ink',
  rail: 'bg-rail-tile text-rail-text',
  signal: 'bg-signal text-ink'
};
</script>

<template>
  <span
    aria-hidden="true"
    class="type-display flex shrink-0 items-center justify-center"
    :class="toneClasses[tone]"
    :style="{ width: `${size}px`, height: `${size}px`, borderRadius: radiusBySize[size], fontSize: fontBySize[size], lineHeight: 1 }"
  >
    {{ initial }}
  </span>
</template>
